import {
  AGENT_CONTRACT, GLOBAL_SIGNALS, SCREENS, isListParam, paramDefault, paramValues, screenReduxActions, screenSignals,
} from '../lib/agent/discovery';
import { paramsFor, screenForPath } from '../lib/agent/screens';
import { resolveOption } from '../lib/agent/options';
import { readScreen } from '../lib/screenDigest';

/**
 * What the user is looking at, in the shape the agent already reads.
 *
 * The worker parses a `lk.chat` message as JSON and, when it is an object,
 * takes `text` as the question and merges `context` into the turn's metadata
 * (`livekit_worker.create_text_input_handler`). Inside that object the field
 * that matters most is `screen_security`: the backend calls it "the FE's
 * explicit answer, and it wins" — honoured on any screen, ahead of everything
 * it would otherwise infer. So the route's own subject goes there, which is
 * what lets "this fund", "buy this one" and "why is it behind?" resolve to the
 * thing on screen rather than to whatever was last mentioned.
 *
 * Derived from the URL and the agent catalogue
 * (`config/pms_ui_discovery_summary.json`, through `lib/agent/discovery.js`)
 * rather than collected from each page, because the URL is the one thing every
 * screen already agrees on — tabs and controls live there too, so the context
 * names the tab the reader is on without the page having to say so. A page with
 * more to say adds it through `useScreenContext` — see `api/copilot.js`.
 */

/**
 * The backend blobs any string longer than 500 characters into a reference the
 * model cannot read, so every string is held under that with room to spare.
 */
const MAX_STRING = 480;

/* Facts are meant to be a handful of short values; these bound a page that
   publishes a table by mistake, so one screen cannot crowd out the question. */
const MAX_FACT_ITEMS = 20;
const MAX_FACT_DEPTH = 3;

/**
 * The thing each detail screen is about, by the route param that names it.
 * A security goes to `screen_security`; a client also scopes the backend to
 * that client through `target_user_id`.
 */
const SUBJECTS = {
  fund: { param: 'id', kind: 'security', resolved: 'securitySymbol' },
  stock: { param: 'symbol', kind: 'security' },
  /* The Stock workspace names its subject in the query string. */
  deskStock: { param: 'symbol', kind: 'security' },
  client360: { param: 'id', kind: 'client', resolved: 'clientId' },
  kycClient: { param: 'id', kind: 'client', resolved: 'clientId' },
  goal: { param: 'goalId', kind: 'goal' },
  model: { param: 'id', kind: 'model' },
  manager: { param: 'name', kind: 'manager' },
};

const isPlainObject = (v) => v != null && typeof v === 'object' && !Array.isArray(v);

const clampString = (s) => (s.length > MAX_STRING ? `${s.slice(0, MAX_STRING - 1)}…` : s);

/**
 * A copy with every empty value dropped and every string clamped.
 *
 * Empty means null, undefined, '', an empty list or an object left with no
 * keys — each one a question the agent would otherwise spend a sentence on.
 * Numbers that are not finite and functions are dropped for the same reason.
 * Inside a list that keeps anything, an empty item stays as null instead:
 * positions carry meaning in `screen_content` (a cell sits under its column, a
 * row's `refs` entry beside the row), and dropping one shifted every later
 * item onto the wrong row. `limits` bounds depth and list length for
 * page-supplied facts; the context's own fields are already small.
 */
function compact(value, { depth = 0, maxDepth = Infinity, maxItems = Infinity } = {}) {
  if (value == null) return undefined;
  if (typeof value === 'string') {
    const s = value.trim();
    return s ? clampString(s) : undefined;
  }
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'boolean') return value;
  if (depth >= maxDepth) return undefined;
  if (Array.isArray(value)) {
    const items = value
      .slice(0, maxItems)
      .map((v) => compact(v, { depth: depth + 1, maxDepth, maxItems }));
    return items.some((v) => v !== undefined) ? items.map((v) => (v === undefined ? null : v)) : undefined;
  }
  if (isPlainObject(value)) {
    const out = {};
    Object.entries(value).forEach(([k, v]) => {
      const kept = compact(v, { depth: depth + 1, maxDepth, maxItems });
      if (kept !== undefined) out[k] = kept;
    });
    return Object.keys(out).length ? out : undefined;
  }
  return undefined;
}

/** The DOM digest, or nothing where there is no DOM to read (a test, a Node check, a first paint). */
function readScreenContent() {
  if (typeof document === 'undefined') return undefined;
  try {
    return readScreen() || undefined;
  } catch {
    /* The digest is a courtesy. A page mid-render must never cost the question. */
    return undefined;
  }
}

/**
 * The actions usable anywhere: each global signal, with the form updates the
 * screen that declares it lists (the watchlist form can be filled from any
 * screen, because it is submitted from any screen).
 */
const GLOBAL_ACTIONS = (() => {
  const out = [];
  Object.keys(SCREENS).forEach((key) => {
    if (!screenSignals(key).some((type) => GLOBAL_SIGNALS.includes(type))) return;
    screenReduxActions(key).forEach((type) => { if (!out.includes(type)) out.push(type); });
  });
  GLOBAL_SIGNALS.forEach((type) => { if (!out.includes(type)) out.push(type); });
  return out;
})();

/** The screen id owning a path, or null for one no route claims. Kept for existing callers. */
export const screenIdFor = (pathname) => screenForPath(pathname);

/**
 * The context object to ride along with a question, and the screen name the
 * frame is sent under.
 *
 * - `params`: the route params and declared query params in the URL.
 * - the subject (the param `SUBJECTS` names, unless the page published the id
 *   it actually resolved — `securitySymbol` on FundDeepDive, `clientId` on
 *   ClientDeepDive): a security goes to `screen_security`, anything else to
 *   `subject`; a client in scope also sets
 *   `target_user_id` — the client screen's own id, else a `clientId` the page
 *   resolved and published, else a `client` param (goal, KYC, the goal
 *   planner), else a `client` the page published — so the backend can act
 *   for that client.
 * - `tab`: the tab the page published it rendered, else the URL's, else the
 *   default the catalogue states. `controls`: the other enumerated params, as
 *   the URL sets them or at their stated default — the default is what is on
 *   screen.
 * - `available_actions`: the `UPDATE_*` and `REQUEST_*` types the catalogue
 *   declares for this screen, then the ones usable anywhere.
 * - `facts`: what the page published. `name` belongs to the subject, so it is
 *   moved there; everything else stays under `facts`, clamped.
 * - `screen_content`: the DOM digest, last, so the page's own facts stay ahead
 *   of anything scraped.
 */
export function screenContextFor({ pathname, search = '', asOf, facts } = {}) {
  let path = typeof pathname === 'string' ? pathname : '';
  let query = search;
  /* A caller that hands over `location.pathname + location.search` in one string
     still gets its query read, rather than silently losing the tab. */
  if (path.includes('?') && !query) {
    [path, query] = [path.slice(0, path.indexOf('?')), path.slice(path.indexOf('?'))];
  }

  const key = path ? screenForPath(path) : null;
  const entry = key ? SCREENS[key] : null;
  const screen = entry?.screen || 'Unknown';
  const params = key ? paramsFor(key, path, query) : {};
  const pageFacts = compact(facts, { maxDepth: MAX_FACT_DEPTH, maxItems: MAX_FACT_ITEMS }) || {};

  const context = {
    contract: AGENT_CONTRACT,
    surface: 'pms_web',
    currentScreen: screen,
    path,
    asOf,
    params,
  };

  const subjectDef = key ? SUBJECTS[key] : null;
  /* The subject the page says it is showing wins over the route's: a screen
     that fell back from the id in the URL (a stale link, the sidebar's demo id
     before it is replaced) must not name that id beside another subject's
     figures and name. The fact is consumed here, not repeated under `facts`. */
  const resolvedFact = subjectDef?.resolved ? pageFacts[subjectDef.resolved] : undefined;
  if (subjectDef?.resolved) delete pageFacts[subjectDef.resolved];
  const resolvedId = typeof resolvedFact === 'string' || typeof resolvedFact === 'number' ? resolvedFact : null;
  const subjectId = resolvedId ?? (subjectDef ? params[subjectDef.param] : null);
  const subjectName = typeof pageFacts.name === 'string' ? pageFacts.name : undefined;
  if (subjectDef && subjectId != null && typeof subjectId !== 'object') {
    const id = String(subjectId);
    if (subjectDef.kind === 'security') {
      context.screen_security = { securitySymbol: id, name: subjectName };
    } else {
      context.subject = { kind: subjectDef.kind, id, name: subjectName };
    }
    if (subjectDef.kind === 'client') context.target_user_id = id;
    delete pageFacts.name;
  }

  /* A client in scope without being the subject, in order of authority:
     - `facts.clientId`: the client the page resolved and is showing. It wins
       over the URL because a page can fall back from the client the URL names
       (CompareFunds measures against the largest book when that one is not
       invested), and the agent must act for the client on screen.
     - the `client` param: GoalDetail's owner, the KYC client, the client
       GoalPlanning's planner is open for.
     - `facts.client`: a client the page shows without the URL naming one
       (GoalPlanning's picked client). */
  if (!context.target_user_id) {
    const idLike = (v) => (typeof v === 'string' || typeof v === 'number' ? v : undefined);
    const id = idLike(pageFacts.clientId) ?? params.client ?? idLike(pageFacts.client);
    if (id != null) context.target_user_id = String(id);
  }

  const tabValues = key ? paramValues(key, 'tab') : null;
  if (tabValues) {
    /* The tab the page says it rendered wins over the URL. A page that keeps
       its tab in local state lets the URL name a tab nobody is looking at
       (after the reader clicks another one, or an agent navigate set `?tab=`
       the page ignores); a page reading the URL publishes the same tab, so
       for those nothing changes. */
    const fromFacts = typeof pageFacts.tab === 'string' ? resolveOption(pageFacts.tab, tabValues) : null;
    context.tab = fromFacts ?? params.tab ?? paramDefault(key, 'tab') ?? undefined;
  }

  const controls = {};
  Object.keys(entry?.values || {}).forEach((name) => {
    if (name === 'tab' || name === subjectDef?.param) return;
    const stated = paramDefault(key, name);
    const fallback = stated != null && isListParam(key, name) ? stated.split(',') : stated;
    const value = params[name] ?? fallback;
    if (value != null) controls[name] = value;
  });
  context.controls = controls;

  const actions = [...screenReduxActions(key), ...screenSignals(key)];
  GLOBAL_ACTIONS.forEach((type) => { if (!actions.includes(type)) actions.push(type); });
  context.available_actions = actions;

  context.facts = pageFacts;
  context.screen_content = readScreenContent();

  return { screen, context: compact(context) || { currentScreen: screen } };
}
